import { useAnalytics } from "@codat/analytics";
import {
  Autosuggest,
  AutosuggestItem,
  FlagIcon,
  ModalBody,
  Typography,
  countries,
} from "@codat/orchard-ui";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import Button from "components/Button";
import SelectInput from "components/SelectInput";

import styles from "./Onboarding.module.css";

interface CompanyNameModalProps {
  companyName: string | undefined;
  onContinue: () => void;
  loading: boolean;
}

export const CompanyNameModal = ({
  companyName,
  onContinue,
  loading,
}: CompanyNameModalProps) => {
  const {
    handleSubmit,
    formState: { isValid },
  } = useFormContext();
  const { sendEvent } = useAnalytics();

  const [countrySelectItems, setCountrySelectItems] = useState<
    AutosuggestItem[]
  >([]);

  useEffect(() => {
    const items = countries.map((country) => ({
      id: country.flagName,
      label: country.name,
      value: country.alpha2code,
    }));

    const moveItemsToFrontOfArray = (strings: string[]) => [
      ...items.filter((item) => strings.includes(item.value)),
      ...items.filter((item) => !strings.includes(item.value)),
    ];

    setCountrySelectItems(moveItemsToFrontOfArray(["US", "GB", "AU"]));
  }, []);

  return (
    <ModalBody
      size="medium"
      title=""
      className={styles.modal}
      data-testid="company-name-modal"
    >
      <Typography
        as="h1"
        variant="heading_xl"
        className={styles.modalTitle}
        isSkeleton={!companyName}
      >
        <span>Tell us about </span>
        {companyName}
      </Typography>
      <Typography
        as="h2"
        variant="body_sm"
        className={styles.modalSubtitle}
        isSkeleton={!companyName}
      >
        This will help us set up your Codat experience
      </Typography>
      <form onSubmit={handleSubmit(onContinue)} className={styles.form}>
        <Controller
          name="countryCode"
          rules={{
            required: true,
          }}
          render={({ field }) => {
            return (
              <Autosuggest
                id="country-selector"
                label={`Where is ${companyName || "your company"} based?`}
                items={countrySelectItems}
                renderIcon={(item) => <FlagIcon country={item.id} />}
                showLabel
                handleSelectedItemChange={(selectedItem) => {
                  field.onChange(selectedItem?.value);
                  sendEvent("Country - Change", {
                    country: selectedItem?.label,
                  });
                }}
                allowClearInput
                data-testid="form-input-country"
                isSkeleton={countrySelectItems.length === 0 || !companyName}
                ref={field.ref}
                data-autofocus
              />
            );
          }}
        />
        <Controller
          name="employeeRange"
          rules={{ required: true }}
          render={({ field }) => (
            <SelectInput
              inputName="employeeRange"
              label={`How many people work at ${companyName}?`}
              showLabel
              id="employee-range-input"
              items={[
                { label: "1-25", value: "1-25" },
                { label: "26-250", value: "26-250" },
                { label: "250+", value: "250+" },
              ]}
              handleSelectedItemChange={(selectedItem) =>
                field.onChange(selectedItem?.value)
              }
              placeholder="Select"
              data-testid="form-input-employeeRange"
              ref={field.ref}
              isSkeleton={!companyName}
            />
          )}
        />
        <Controller
          name="jobTitle"
          rules={{
            required: true,
          }}
          render={({ field }) => {
            return (
              <SelectInput
                inputName="Department"
                label="What is your department?"
                showLabel
                id="job-title-input"
                items={[
                  { label: "Technology", value: "Technology" },
                  { label: "Product", value: "Product" },
                  { label: "Support", value: "Support" },
                  { label: "Analyst", value: "Analyst" },
                  { label: "Other", value: "Other" },
                ]}
                handleSelectedItemChange={(selectedItem) =>
                  field.onChange(selectedItem?.value)
                }
                placeholder="Select"
                data-testid="form-input-jobTitle"
                ref={field.ref}
                isSkeleton={!companyName}
              />
            );
          }}
        />
        <footer className={styles.modalFooter}>
          <Button
            label="Continue"
            buttonName="Continue"
            className={styles.primaryBtn}
            disabled={!isValid || loading}
            isLoading={loading}
            aria-busy={loading || !companyName}
            isSkeleton={!companyName}
            data-testid="continue-button"
          />
        </footer>
      </form>
    </ModalBody>
  );
};
